import { BaseStateInterface } from './basestate.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { QuestionInstanceDto } from 'app/@types/dto/question-instance-dto.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { Subject, takeUntil } from 'rxjs';
import { QuestionnaireConfigState } from './questionnaire.config.state';
import { QuestionnaireConfig } from 'app/@types/dto/questionnaire-config.model';
import { QuestionDto } from 'app/@types/dto/question-dto.model';
import { FormulaireCallCompletion } from 'app/@types/shared/formulaire-call-completion';
import { FeatureService } from '@services/features/feature.service';
import { ResultWebSocketService } from '@services/datasources/result-web-socket.service';
import { HubAccessDeniedError } from '@services/datasources/hub-access-denied.error';
import { SolutionAction } from './solution.state';
import { QuestionnaireDimensionDto } from 'app/@types/dto/questionnaire-dimension-dto.model';
export interface QuestionnaireModel extends BaseStateInterface {
    questionnaire: QuestionnaireDto | undefined;
    questionInstancePerQuestionCode: Map<string, QuestionInstanceDto[]> | undefined;
    visitedPage: string | undefined;
    externalUpdates: Set<string>;
    summaryDoc: { [dimension: string]: number };
    // True once the user has explicitly clicked a cell in the measure-summary matrix.
    // The login auto-activation in AppComponent.handleUserClaims leaves this false, so the
    // app menu's "Measure" item routes to the matrix until the user has actively picked a
    // scan. Reset on Deactivate (logout, org switch).
    cellSelected: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace QuestionnaireAction {
    export class Activate {
        public static type = '[Questionnaire UI] Activate Questionnaire';

        constructor(
            public organizationId: string,
            public year: number,
            public entityId: string,
            public isOnboarding?: boolean,
            public onboardingAwaiter?: Subject<boolean>,
        ) {}
    }

    export class CreateUpdateAction {
        public static type = '[Questionnaire UI] CreateUpdate QuestionInstance';

        constructor(
            public organizationId: string,
            public instance: QuestionInstanceDto,
            public questionField: number,
            public callCompletion: Subject<QuestionInstanceDto | undefined>,
        ) {}
    }

    export class DeleteAction {
        public static type = '[Questionnaire UI] Delete QuestionInstance';

        constructor(
            public organizationId: string,
            public instance: QuestionInstanceDto,
            public callCompletion: Subject<QuestionInstanceDto[] | undefined>,
        ) {}
    }

    export class SetVisitedPage {
        public static type = '[Questionnaire UI] SetVisitedPage';

        constructor(public pageCode: string) {}
    }

    export class UpdatePropertyAction {
        public static type = '[Questionnaire UI] Update Questionnaire';

        constructor(
            public organizationId: string,
            public instance: QuestionnaireDto,
            public callCompletion: Subject<FormulaireCallCompletion>,
        ) {}
    }

    export class ActivateSummaryDoc {
        public static type = '[Questionnaire UI] Activate Summary Doc';
        constructor(
            public organizationId: string,
            public questionnaireId: string,
        ) {}
    }

    export class UpdateSummaryDoc {
        public static type = '[Questionnaire UI] Update Summary Doc';

        constructor(
            public dimension: string,
            public value: number,
        ) {}
    }

    export class InvalidateSummaryDoc {
        public static type = '[Questionnaire UI] Invalidate Summary Doc';
    }

    export class Deactivate {
        public static type = '[Questionnaire UI] Deactivate Questionnaire';
    }

    export class MarkCellSelected {
        public static type = '[Questionnaire UI] Mark Cell Selected';
    }

    export class UnmarkCellSelected {
        public static type = '[Questionnaire UI] Unmark Cell Selected';
    }

    export class CreateQuestionnaireDimension {
        public static type = '[Questionnaire UI] Create Questionnaire Dimension';

        constructor(
            public organizationId: string,
            public questionnaireDimension: QuestionnaireDimensionDto,
            public callCompletion: Subject<boolean>,
        ) {}
    }

    export class UpdateQuestionnaireDimension {
        public static type = '[Questionnaire UI] Update Questionnaire Dimension';

        constructor(
            public organizationId: string,
            public questionnaireDimension: QuestionnaireDimensionDto,
            public callCompletion: Subject<boolean>,
        ) {}
    }
}

@State<QuestionnaireModel>({
    name: 'questionnaire',
    defaults: {
        questionnaire: undefined,
        questionInstancePerQuestionCode: undefined,
        visitedPage: undefined,
        isInit: false,
        isLoading: false,
        externalUpdates: new Set<string>(),
        errors: [],
        summaryDoc: {},
        cellSelected: false,
    },
})
@Injectable()
export class QuestionnaireState {
    private isInit: boolean = false;
    private readonly activateTeardown$ = new Subject<void>();

    constructor(
        private questionnaireService: QuestionnaireHttpService,
        private store: Store,
        private resultWebSocketService: ResultWebSocketService,
        private readonly featureService: FeatureService,
    ) {}

    //----- store action -----//

    @Action(QuestionnaireAction.Activate)
    activate(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.Activate) {
        // Always tear down any in-flight Activate and start the new one. Previously this
        // was gated by `if (!isLoading || isOnboarding)`, which silently dropped subsequent
        // Activate dispatches while a fetch was running. That dropped click let the prior
        // request "win" — landing into state — even though the user had just navigated to a
        // different cell, causing the second scan/expert to appear to show the first one's
        // data ("scan A → summary → scan B → summary → scan A shows B").
        this.activateTeardown$.next();
        const untilQ = this.activateTeardown$;
        ctx.patchState({
            isLoading: true,
        });
        const currentQuestionnaireId = ctx.getState().questionnaire?.id;
        if (currentQuestionnaireId) {
            this.resultWebSocketService.leaveQuestionnaireGroup(currentQuestionnaireId);
        }
        this.questionnaireService
            .get(action.organizationId, action.year, action.entityId)
            .pipe(takeUntil(untilQ))
            .subscribe((qqi) => {
                if (qqi) {
                    const questionMap = qqi.questionInstances.reduce((reducer, qi) => {
                        const entries = reducer.get(qi.questionCode);
                        if (entries === undefined) {
                            reducer.set(qi.questionCode, [qi]);
                        } else {
                            entries.push(qi);
                        }

                        return reducer;
                    }, new Map<string, QuestionInstanceDto[]>());

                    const updateQuestionnaire = qqi.questionnaire;
                    // Lock is active only on Scan (typeId 1) — the flag persists but is dormant on
                    // Expert. Mirror the getIsLocked selector so a dormant flag never blocks the
                    // lastUpdate write on an Expert questionnaire. Also skip the write for read-only
                    // Viewers — the backend rejects questionnaire_rw for them (403).
                    const isActivelyLocked = updateQuestionnaire.typeId === 1 && updateQuestionnaire.isLocked === true;
                    if (!isActivelyLocked && !this.featureService.isReadOnly()) {
                        updateQuestionnaire.lastUpdate = new Date().toISOString();
                        this.questionnaireService
                            .updateProperty(action.organizationId, updateQuestionnaire)
                            .pipe(takeUntil(untilQ))
                            .subscribe({});
                    }
                    ctx.patchState({
                        questionnaire: updateQuestionnaire,
                        questionInstancePerQuestionCode: questionMap,
                        summaryDoc: qqi.questionnaire.summaryDoc,
                        isInit: true,
                        isLoading: false,
                        errors: undefined,
                    });
                    if (action.onboardingAwaiter) {
                        action.onboardingAwaiter.next(true);
                    }
                    if (qqi.questionnaire.id) {
                        const qId = qqi.questionnaire.id;
                        this.resultWebSocketService
                            .startConnection()
                            .then(() => this.resultWebSocketService.joinQuestionnaireGroup(qId))
                            .catch((err: unknown) => {
                                if (err instanceof HubAccessDeniedError) {
                                    console.warn(
                                        '[QuestionnaireState] SignalR questionnaire group join denied:',
                                        err.message,
                                    );
                                }
                            });
                        if (qqi.questionnaire.typeId === 1) {
                            this.store.dispatch(
                                new SolutionAction.Activate(action.organizationId, qqi.questionnaire.id),
                            );
                        }
                    }
                    this.isInit = true;
                } else {
                    const errors = ctx.getState().errors;
                    errors.push('Cannot fetch the questionnaire related entities');

                    ctx.patchState({
                        questionnaire: undefined,
                        questionInstancePerQuestionCode: new Map<string, QuestionInstanceDto[]>(),
                        isInit: true,
                        isLoading: false,
                        errors: errors,
                    });

                    if (action.isOnboarding && action.onboardingAwaiter) {
                        action.onboardingAwaiter.next(false);
                    }
                }
            });
    }

    @Action(QuestionnaireAction.Deactivate)
    deactivate(ctx: StateContext<QuestionnaireModel>, _action: QuestionnaireAction.Deactivate) {
        this.activateTeardown$.next();
        const questionnaireId = ctx.getState().questionnaire?.id;
        if (questionnaireId) {
            void this.resultWebSocketService.leaveQuestionnaireGroup(questionnaireId);
        }
        ctx.patchState({
            questionnaire: undefined,
            questionInstancePerQuestionCode: undefined,
            visitedPage: undefined,
            isInit: false,
            isLoading: false,
            externalUpdates: new Set<string>(),
            summaryDoc: {},
            errors: [],
            cellSelected: false,
        });
        this.isInit = false;
    }

    @Action(QuestionnaireAction.MarkCellSelected)
    markCellSelected(ctx: StateContext<QuestionnaireModel>) {
        ctx.patchState({ cellSelected: true });
    }

    @Action(QuestionnaireAction.UnmarkCellSelected)
    unmarkCellSelected(ctx: StateContext<QuestionnaireModel>) {
        ctx.patchState({ cellSelected: false });
    }

    @Action(QuestionnaireAction.CreateUpdateAction)
    createOrUpdate(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.CreateUpdateAction) {
        this.questionnaireService.createUpdate(action.organizationId, action.instance).subscribe({
            next: (qia) => {
                const questions = new Map([
                    ...(ctx.getState().questionInstancePerQuestionCode ?? new Map<string, QuestionInstanceDto[]>()),
                ]);
                const config = this.store.selectSnapshot(QuestionnaireConfigState.getStaticConfig);
                if (qia && qia.length !== 0 && config) {
                    this.handleQuestionInstanceUpdate(config, questions, qia);
                    const externalUpdatedSet = new Set(
                        qia
                            .filter((qi) => qi.questionCode !== action.instance.questionCode)
                            .map((qi) => qi.questionCode),
                    );
                    ctx.patchState({
                        questionInstancePerQuestionCode: questions,
                        externalUpdates: externalUpdatedSet,
                    });
                    action.callCompletion.next(qia[0]);
                }
            },
            error: () => {
                const questions = new Map([
                    ...(ctx.getState().questionInstancePerQuestionCode ?? new Map<string, QuestionInstanceDto[]>()),
                ]);
                action.callCompletion.next(undefined);
                ctx.patchState({
                    questionInstancePerQuestionCode: questions,
                });
            },
        });
    }

    handleQuestionInstanceUpdate(
        config: QuestionnaireConfig,
        stateQuestionMapPerQuestionCode: Map<string, QuestionInstanceDto[]>,
        questionInstanceToUpdate: QuestionInstanceDto[],
    ): void {
        const questionTemplateByQuestionCode = Object.values(config.questionsPerPage)
            .flatMap((a) => Object.values(a).flatMap((qa) => qa))
            .reduce((map, qt) => map.set(qt.questionCode, qt), new Map<string, QuestionDto>());

        questionInstanceToUpdate.forEach((qi) => {
            const qt = questionTemplateByQuestionCode.get(qi.questionCode);
            const currentQuestionArray = stateQuestionMapPerQuestionCode.get(qi.questionCode);
            if (qt) {
                if (qt.isDynamic || qt.option?.isSubQuestion) {
                    if (currentQuestionArray) {
                        const index = qt.isDynamic
                            ? currentQuestionArray.findIndex((e) => e.value.value1 === qi.value.value1)
                            : currentQuestionArray.findIndex(
                                  (x) => x.relatedQuestionInstanceId === qi.relatedQuestionInstanceId,
                              );
                        if (index !== -1) {
                            currentQuestionArray.splice(index, 1, qi);
                        } else {
                            currentQuestionArray.push(qi);
                        }
                    } else {
                        stateQuestionMapPerQuestionCode.set(qi.questionCode, [qi]);
                    }
                } else {
                    stateQuestionMapPerQuestionCode.set(qi.questionCode, [qi]);
                }
            }
        });
    }

    @Action(QuestionnaireAction.DeleteAction)
    delete(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.DeleteAction) {
        this.questionnaireService.delete(action.organizationId, action.instance).subscribe({
            next: (qia) => {
                const questions = new Map([
                    ...(ctx.getState().questionInstancePerQuestionCode ?? new Map<string, QuestionInstanceDto[]>()),
                ]);
                const config = this.store.selectSnapshot(QuestionnaireConfigState.getStaticConfig);
                if (qia && config) {
                    if (action.instance.id) {
                        this.handleQuestionInstanceDelete(config, questions, qia);
                    }
                    action.callCompletion.next(qia);
                    const externalUpdatedSet = new Set(
                        qia
                            .filter((qi) => qi.questionCode !== action.instance.questionCode)
                            .map((qi) => qi.questionCode),
                    );
                    ctx.patchState({
                        questionInstancePerQuestionCode: questions,
                        externalUpdates: externalUpdatedSet,
                    });
                }
            },
            error: () => {
                const questions = new Map([
                    ...(ctx.getState().questionInstancePerQuestionCode ?? new Map<string, QuestionInstanceDto[]>()),
                ]);
                action.callCompletion.next(undefined);
                ctx.patchState({
                    questionInstancePerQuestionCode: questions,
                });
            },
        });
    }

    handleQuestionInstanceDelete(
        config: QuestionnaireConfig,
        stateQuestionMapPerQuestionCode: Map<string, QuestionInstanceDto[]>,
        questionInstanceToUpdate: QuestionInstanceDto[],
    ): void {
        const questionTemplateByQuestionCode = Object.values(config.questionsPerPage)
            .flatMap((a) => Object.values(a).flatMap((qa) => qa))
            .reduce((map, qt) => map.set(qt.questionCode, qt), new Map<string, QuestionDto>());
        questionInstanceToUpdate.forEach((qi) => {
            const qt = questionTemplateByQuestionCode.get(qi.questionCode);
            const currentQuestionArray = stateQuestionMapPerQuestionCode.get(qi.questionCode);
            if (qt) {
                if (qt.isDynamic || qt.option?.isSubQuestion) {
                    if (currentQuestionArray) {
                        const index = qt.isDynamic
                            ? currentQuestionArray.findIndex((e) => e.value.value1 === qi.value.value1)
                            : currentQuestionArray.findIndex(
                                  (x) => x.relatedQuestionInstanceId === qi.relatedQuestionInstanceId,
                              );
                        if (index !== -1) {
                            currentQuestionArray.splice(index, 1);
                        }
                    }
                } else {
                    stateQuestionMapPerQuestionCode.delete(qi.questionCode);
                }
            }
        });
    }

    @Action(QuestionnaireAction.UpdatePropertyAction)
    UpdateProperty(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.UpdatePropertyAction) {
        this.questionnaireService.updateProperty(action.organizationId, action.instance).subscribe({
            next: (updateQuestionnaire) => {
                ctx.patchState({ questionnaire: updateQuestionnaire });
                if (action.callCompletion) {
                    action.callCompletion.next('questSuccess');
                    action.callCompletion.complete();
                }
            },
            error: () => {
                if (action.callCompletion) {
                    action.callCompletion.next('questFail');
                    action.callCompletion.complete();
                }
            },
        });
    }

    @Action(QuestionnaireAction.SetVisitedPage)
    setVisitedQuestionCode(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.SetVisitedPage) {
        ctx.patchState({
            visitedPage: action.pageCode,
        });
    }

    @Action(QuestionnaireAction.ActivateSummaryDoc)
    activateSummaryDoc(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.ActivateSummaryDoc) {
        if (ctx.getState().summaryDoc) {
            return;
        }
        this.questionnaireService
            .getSummaryDoc(action.organizationId, action.questionnaireId)
            .subscribe((summaryDoc) => {
                ctx.patchState({
                    summaryDoc: summaryDoc,
                });
            });
    }

    @Action(QuestionnaireAction.UpdateSummaryDoc)
    updateSummaryDoc(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.UpdateSummaryDoc) {
        const summaryDoc = ctx.getState().summaryDoc;
        summaryDoc[action.dimension] ??= 0;
        summaryDoc[action.dimension] += action.value;
        ctx.patchState({ summaryDoc });
    }

    @Action(QuestionnaireAction.InvalidateSummaryDoc)
    invalidateSummaryDoc(ctx: StateContext<QuestionnaireModel>, _action: QuestionnaireAction.InvalidateSummaryDoc) {
        ctx.patchState({
            summaryDoc: undefined,
        });
    }

    @Action(QuestionnaireAction.CreateQuestionnaireDimension)
    createQuestionnaireDimension(
        ctx: StateContext<QuestionnaireModel>,
        action: QuestionnaireAction.CreateQuestionnaireDimension,
    ) {
        this.questionnaireService
            .createQuestionnaireDimension(action.organizationId, action.questionnaireDimension)
            .subscribe({
                next: (questionnaireDimension) => {
                    const questionnaire = ctx.getState().questionnaire;
                    if (questionnaire) {
                        // Create a new object reference to trigger change detection
                        const updatedQuestionnaire = {
                            ...questionnaire,
                            questionnaireDimensions: [...questionnaire.questionnaireDimensions, questionnaireDimension],
                        };
                        ctx.patchState({ questionnaire: updatedQuestionnaire });
                    }
                    action.callCompletion.next(true);
                },
                error: () => {
                    action.callCompletion.next(false);
                },
            });
    }

    @Action(QuestionnaireAction.UpdateQuestionnaireDimension)
    updateQuestionnaireDimension(
        ctx: StateContext<QuestionnaireModel>,
        action: QuestionnaireAction.UpdateQuestionnaireDimension,
    ) {
        this.questionnaireService
            .updateQuestionnaireDimension(action.organizationId, action.questionnaireDimension)
            .subscribe({
                next: (questionnaireDimension) => {
                    const questionnaire = ctx.getState().questionnaire;
                    if (questionnaire) {
                        // Create a new object reference to trigger change detection
                        const updatedQuestionnaire = {
                            ...questionnaire,
                            questionnaireDimensions: questionnaire.questionnaireDimensions.map((dimension) =>
                                dimension.dimension === action.questionnaireDimension.dimension
                                    ? questionnaireDimension
                                    : dimension,
                            ),
                        };
                        ctx.patchState({ questionnaire: updatedQuestionnaire });
                    }
                    action.callCompletion.next(true);
                },
                error: () => {
                    action.callCompletion.next(false);
                },
            });
    }

    @Selector([QuestionnaireState])
    static getQuestionInstances(state: QuestionnaireModel) {
        return state.questionInstancePerQuestionCode;
    }

    @Selector([QuestionnaireState])
    static getQuestionnaire(state: QuestionnaireModel) {
        return state.questionnaire;
    }

    @Selector([QuestionnaireState])
    static getIsLoading(state: QuestionnaireModel): boolean {
        return state.isLoading;
    }

    @Selector([QuestionnaireState])
    static getVisitedPage(state: QuestionnaireModel) {
        return state.visitedPage;
    }

    @Selector([QuestionnaireState])
    static getExternalUpdate(state: QuestionnaireModel) {
        return state.externalUpdates;
    }

    @Selector([QuestionnaireState])
    static getSummaryDoc(state: QuestionnaireModel) {
        return state.summaryDoc;
    }

    @Selector([QuestionnaireState])
    static getIsLocked(state: QuestionnaireModel): boolean {
        // Lock is active only on Scan questionnaires (typeId 1). The isLocked flag persists across
        // conversions but is dormant while the questionnaire is Expert.
        return state.questionnaire?.typeId === 1 && state.questionnaire?.isLocked === true;
    }

    @Selector([QuestionnaireState])
    static hasCellBeenSelected(state: QuestionnaireModel): boolean {
        return state.cellSelected;
    }
}
